"use client";

/**
 * Client body for the two marketing category hubs (/use-cases, /integrations).
 *
 * A crawlable index page: hero → grid of child links → CTA. Lives apart from
 * its `page.tsx` (server component, for `metadata` + JSON-LD) so it can follow
 * the visitor's locale via `useT()`, exactly like every other marketing page.
 * All copy comes from the `marketingHubs.<hub>` namespace; the child list
 * (hrefs + item keys) is passed in by the page.
 */

import { MarketingShell } from "@/components/marketing/marketing-shell";
import {
  UseCaseHero,
  Section,
  RelatedLinks,
  CtaBanner,
} from "@/components/marketing/marketing-kit";
import { useT } from "@/i18n";

export type HubChild = { href: string; itemKey: string };

export function MarketingHubContent({
  hub,
  items,
  primary,
  secondary,
  beforeGrid,
  children,
}: {
  /** i18n sub-namespace under `marketingHubs`, e.g. "useCases". */
  hub: string;
  items: HubChild[];
  primary: { href: string };
  secondary: { href: string };
  /** Rendered above the link grid, inside the same section (e.g. the
   *  demo-video band on /use-cases). */
  beforeGrid?: React.ReactNode;
  /** Extra sections rendered between the grid and the CTA (e.g. the
   *  integration-status matrix on /integrations). */
  children?: React.ReactNode;
}) {
  const t = useT();
  const k = (s: string) => `marketingHubs.${hub}.${s}`;

  const links = items.map((c) => ({
    href: c.href,
    label: t(`${k("items")}.${c.itemKey}.title`),
    description: t(`${k("items")}.${c.itemKey}.desc`),
  }));

  const ctaLabels = {
    primary: { href: primary.href, label: t(k("ctaPrimary")) },
    secondary: { href: secondary.href, label: t(k("ctaSecondary")) },
  };

  return (
    <MarketingShell>
      <UseCaseHero
        eyebrow={t(k("eyebrow"))}
        title={t(k("title"))}
        highlight={t(k("highlight"))}
        subtitle={t(k("subtitle"))}
        primary={ctaLabels.primary}
        secondary={ctaLabels.secondary}
      />
      <Section>
        {beforeGrid}
        <RelatedLinks title={t(k("relatedTitle"))} links={links} />
      </Section>
      {children}
      <CtaBanner
        title={t(k("ctaTitle"))}
        description={t(k("ctaDescription"))}
        primary={ctaLabels.primary}
        secondary={ctaLabels.secondary}
      />
    </MarketingShell>
  );
}
